import { Box, Heading } from '@chakra-ui/react';
import { ArrowRightIcon } from '@udacity/chakra-uds-icons';

import { ButtonLink } from '~/components/button-link';
import { LogosSection } from '~/components/logos-section';
import { Markdown } from '~/components/markdown';
import { SanityImage } from '~/components/sanity-image';
import { SchoolsCardSection } from '~/components/schools-card-section';
import { ShapesBanner } from '~/components/shapes-banner';
import { StandardHeroSection } from '~/components/standard-hero';
import { UdacityOutcomesSection } from '~/components/udacity-outcomes-section';
import { trackEvent } from '~/features/analytics/track-event';
import { useI18n } from '~/hooks/use-i18n';

import { GovernmentOverviewTemplate } from '../model';

import { CountriesSection } from './countries-section';
import { SuccessStoriesSection } from './success-stories-section';
import { TalentTransformationSuiteSection } from './talent-transformation-section';
import { TransformationStatsSection } from './transformation-stats-section';

export function GovernmentOverview({ pageTemplateData }: { pageTemplateData: GovernmentOverviewTemplate }) {
  const { isRtl } = useI18n();
  const {
    schools,
    schoolsSectionTitle,
    schoolsSectionDescription,
    transformationSectionText,
    transformationSectionStats,
    shapesBanner,
  } = pageTemplateData;
  return (
    <Box bg='white'>
      <StandardHeroSection
        backgroundTheme='light'
        backgroundImageSlot={
          <Box
            bottom='0px'
            pointerEvents='none'
            position='absolute'
            left={
              isRtl
                ? {
                    base: 'calc(50% - 274px)',
                    md: 'calc((100vw - 100rem)/2)',
                  }
                : {
                    base: '0px',
                    md: 'calc((100vw - 70rem)/2)',
                  }
            }
            right={
              isRtl
                ? {
                    base: '0px',
                    md: 'calc((100vw - 70rem)/2)',
                  }
                : {
                    base: 'calc(50% - 274px)',
                    md: 'calc((100vw - 100rem)/2)',
                  }
            }
            top={{
              base: 'calc(50% + 24px)',
              md: '0px',
            }}
          >
            <Box
              height='100%'
              left='0px'
              position='absolute'
              right='0px'
              width={{
                base: '100%',
                md: '1280px',
              }}
            >
              <SanityImage
                alt=''
                image={pageTemplateData.heroImage}
                sizes='1280px'
                style={{
                  objectFit: 'cover',
                  objectPosition: 'right',
                  transform: isRtl ? 'scaleX(-1)' : 'unset',
                }}
                fill
                priority
              />
            </Box>
          </Box>
        }
        contentSlot={
          <>
            {pageTemplateData.pageTitle && (
              <Heading as='h1' size='marketing-5xl'>
                {pageTemplateData.pageTitle}
              </Heading>
            )}
            {pageTemplateData.pageSubtitle && (
              <Heading as='h2' order='-1' size='h4'>
                {pageTemplateData.pageSubtitle}
              </Heading>
            )}
            {pageTemplateData.pageDescription && <Markdown markdownText={pageTemplateData.pageDescription} />}
            <ButtonLink
              buttonProps={{
                variant: 'solid',
                size: 'icon-right',
                rightIcon: <ArrowRightIcon h='24px' w='24px' />,
                width: {
                  base: '100%',
                  md: 'fit-content',
                },
                marginTop: '48px',
                onClick: () => {
                  trackEvent({
                    type: 'click',
                    name: 'Government CTA Clicked',
                    label: 'learn_more_clicked',
                    value: 'CTA 1',
                  });
                },
              }}
              linkProps={{
                href: '/get-started/government',
              }}
            >
              {pageTemplateData.primaryCtaLabel}
            </ButtonLink>
          </>
        }
        imageSlot={
          <Box
            flex={{
              base: '1 0 354px',
              md: '1',
            }}
            height={{
              md: '384px',
            }}
          />
        }
      />
      <LogosSection
        companies={pageTemplateData.partners}
        title={pageTemplateData.partnersSectionTitle}
        containerProps={{
          backgroundColor: 'blue.800',
          color: 'white',
        }}
        logoContainerProps={{
          flex: {
            md: '0 1 800px',
          },
        }}
      />
      <CountriesSection />
      <TransformationStatsSection
        description={transformationSectionText.text}
        stats={transformationSectionStats}
        title={transformationSectionText.title}
      />
      <TalentTransformationSuiteSection
        onCtaClick={() =>
          trackEvent({
            type: 'click',
            name: 'Government CTA Clicked',
            label: 'learn_more_clicked',
            value: 'CTA 2',
          })
        }
      />
      <SchoolsCardSection
        description={schoolsSectionDescription}
        schools={schools}
        title={schoolsSectionTitle}
        onClickSchool={(school) => {
          trackEvent({
            type: 'click',
            name: 'Government CTA Clicked',
            label: 'school_card_clicked',
            value: school.title,
          });
        }}
      />
      <UdacityOutcomesSection
        containerProps={{
          bgGradient: 'linear(to-b, white, accent.sky.100)',
        }}
      />
      <SuccessStoriesSection />
      <ShapesBanner
        {...shapesBanner}
        onPrimaryCtaClick={() =>
          trackEvent({
            type: 'click',
            name: 'Government CTA Clicked',
            label: 'learn_more_clicked',
            value: 'CTA 3',
          })
        }
      />
    </Box>
  );
}
